import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import BoltIcon from '@mui/icons-material/Bolt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SecurityIcon from '@mui/icons-material/Security';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useBudget } from '../../context/BudgetContext';
import { addFixedExpense, removeFixedExpense, updateFixedExpense, batchSetFixedExpenses } from '../../context/budgetActions';
import { toMonthly } from '../../utils/frequency';
import { formatCAD } from '../../utils/formatCurrency';
import {
  FIXED_EXPENSE_CATALOG,
  EXPENSE_LABELS,
  EXPENSE_PRESETS,
  PRESET_LABELS,
} from '../../data/defaultExpenses';

const CATEGORY_ICONS = {
  housing: HomeIcon,
  utilities: BoltIcon,
  transportation: DirectionsCarIcon,
  insurance: SecurityIcon,
  subscriptions: SubscriptionsIcon,
  childcare: ChildCareIcon,
};

const PRESET_ICONS = {
  newcomerSolo: PersonIcon,
  family: FamilyRestroomIcon,
  student: SchoolIcon,
};

function ExpenseToggleCard({ itemKey, catKey, catColor, defaultAmount, frequency, isActive, currentAmount, onToggle, onAmountChange, lng }) {
  const label = EXPENSE_LABELS.items[itemKey]?.[lng] || itemKey;

  return (
    <Box
      onClick={() => onToggle(itemKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: isActive ? `2px solid ${catColor}` : '2px solid #E8E8E8',
        bgcolor: isActive ? `${catColor}08` : '#F7F9FC',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: catColor,
          bgcolor: `${catColor}0C`,
          transform: 'translateY(-1px)',
          boxShadow: 1,
        },
      }}
    >
      {/* Toggle indicator */}
      <Box sx={{ color: isActive ? catColor : '#C0C0C0', flexShrink: 0, display: 'flex' }}>
        {isActive ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
      </Box>

      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: isActive ? 600 : 400,
          color: isActive ? 'text.primary' : 'text.secondary',
          fontSize: '0.85rem',
        }}
      >
        {label}
      </Typography>

      {/* Amount input (inline) */}
      {isActive ? (
        <TextField
          value={currentAmount}
          onChange={(e) => {
            e.stopPropagation();
            onAmountChange(itemKey, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          type="number"
          size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{ min: 0, step: 10, inputMode: 'decimal', style: { textAlign: 'right', width: 72, padding: '4px 0' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 32,
              fontSize: '0.85rem',
              borderRadius: 1.5,
            },
          }}
        />
      ) : (
        <Typography variant="body2" sx={{ color: '#5F6B7A', fontSize: '0.8rem' }}>
          ${defaultAmount}
        </Typography>
      )}
    </Box>
  );
}

function CustomItemInput({ onAdd, lng }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');

  const labels = {
    es: { placeholder: 'Otro gasto...', add: 'Agregar' },
    en: { placeholder: 'Other expense...', add: 'Add' },
    fr: { placeholder: 'Autre d\u00e9pense...', add: 'Ajouter' },
  };
  const l = labels[lng] || labels.es;

  const handleAdd = () => {
    if (name.trim() && amount && Number(amount) > 0) {
      onAdd(name.trim(), Number(amount));
      setName('');
      setAmount('');
    }
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
      <TextField
        placeholder={l.placeholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
        size="small"
        sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 44 } }}
      />
      <Stack direction="row" spacing={1}>
        <TextField
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          size="small"
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          inputProps={{ min: 0, step: 10, inputMode: 'decimal', style: { textAlign: 'right', width: 72 } }}
          sx={{ width: 120, '& .MuiOutlinedInput-root': { height: 44 } }}
        />
        <IconButton
          onClick={handleAdd}
          disabled={!name.trim() || !amount || Number(amount) <= 0}
          aria-label="Agregar gasto"
          sx={{ bgcolor: '#1E3A5F', color: '#fff', borderRadius: 1.5, width: 44, height: 44, '&:hover': { bgcolor: '#1A3152' }, '&.Mui-disabled': { bgcolor: '#E0E0E0', color: '#999' } }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export default function StepFixedExpenses() {
  const { i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const { state, dispatch, derived } = useBudget();

  // Build a map of active items: key → { id, amount }
  const activeItems = useMemo(() => {
    const map = {};
    for (const exp of state.fixedExpenses) {
      if (exp.itemKey) map[exp.itemKey] = { id: exp.id, amount: exp.amount };
    }
    return map;
  }, [state.fixedExpenses]);

  // Custom items (those without itemKey)
  const customItems = state.fixedExpenses.filter((e) => !e.itemKey);

  const handleToggle = (itemKey) => {
    if (activeItems[itemKey]) {
      dispatch(removeFixedExpense(activeItems[itemKey].id));
    } else {
      // Find default
      let defaultAmt = 100;
      let catName = '';
      for (const [catKey, cat] of Object.entries(FIXED_EXPENSE_CATALOG)) {
        const found = cat.items.find((i) => i.key === itemKey);
        if (found) {
          defaultAmt = found.defaultAmount;
          catName = EXPENSE_LABELS.categories[catKey]?.[lng] || catKey;
          break;
        }
      }
      const label = EXPENSE_LABELS.items[itemKey]?.[lng] || itemKey;
      dispatch(addFixedExpense({
        itemKey,
        category: catName,
        name: label,
        amount: defaultAmt,
        frequency: 'monthly',
      }));
    }
  };

  const handleAmountChange = (itemKey, value) => {
    const active = activeItems[itemKey];
    if (active) {
      dispatch(updateFixedExpense({ id: active.id, amount: Number(value) || 0 }));
    }
  };

  const handleAddCustom = (name, amount) => {
    dispatch(addFixedExpense({
      category: EXPENSE_LABELS.categories.other?.[lng] || 'Other',
      name,
      amount,
      frequency: 'monthly',
    }));
  };

  const handlePreset = (presetKey) => {
    const preset = EXPENSE_PRESETS[presetKey];
    if (!preset) return;
    const expenses = [];
    for (const [catKey, cat] of Object.entries(FIXED_EXPENSE_CATALOG)) {
      const catLabel = EXPENSE_LABELS.categories[catKey]?.[lng] || catKey;
      for (const item of cat.items) {
        const amount = preset.fixed[item.key];
        if (amount != null && amount > 0) {
          expenses.push({
            itemKey: item.key,
            category: catLabel,
            name: EXPENSE_LABELS.items[item.key]?.[lng] || item.key,
            amount,
            frequency: 'monthly',
          });
        }
      }
    }
    dispatch(batchSetFixedExpenses(expenses));
  };

  const monthlyTotal = state.fixedExpenses.reduce(
    (sum, e) => sum + toMonthly(e.amount, e.frequency),
    0
  );

  const incomeRatio = derived.monthlyIncome > 0
    ? Math.min(100, (monthlyTotal / derived.monthlyIncome) * 100)
    : 0;

  const ratioColor = incomeRatio > 70 ? '#C62828' : incomeRatio > 50 ? '#7D5A00' : '#2E7D32';

  const presetLabels = {
    es: 'Llenar con perfil:',
    en: 'Quick fill:',
    fr: 'Remplissage rapide\u00a0:',
  };

  const titleLabels = {
    es: 'Gastos fijos',
    en: 'Fixed expenses',
    fr: 'D\u00e9penses fixes',
  };
  const subtitleLabels = {
    es: 'Toca para incluir y ajusta el monto. Usa un perfil para llenar r\u00e1pido.',
    en: 'Tap to include and adjust the amount. Use a preset for quick setup.',
    fr: 'Appuyez pour inclure et ajustez le montant. Utilisez un profil pour remplir rapidement.',
  };

  return (
    <Card elevation={2}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600, color: '#1E3A5F' }}>
          {titleLabels[lng] || titleLabels.es}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitleLabels[lng] || subtitleLabels.es}
        </Typography>

        {/* Preset buttons */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
            {presetLabels[lng] || presetLabels.es}
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            {Object.entries(PRESET_LABELS).map(([key, labels]) => {
              const Icon = PRESET_ICONS[key];
              return (
                <Button
                  key={key}
                  startIcon={Icon ? <Icon sx={{ fontSize: '16px !important' }} /> : null}
                  onClick={() => handlePreset(key)}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#1E3A5F',
                    color: '#1E3A5F',
                    fontSize: '0.78rem',
                    '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F' },
                  }}
                >
                  {labels[lng] || labels.es}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>

        {/* Category sections with toggle cards */}
        {Object.entries(FIXED_EXPENSE_CATALOG).map(([catKey, cat]) => {
          const CatIcon = CATEGORY_ICONS[catKey];
          const catLabel = EXPENSE_LABELS.categories[catKey]?.[lng] || catKey;

          return (
            <Box key={catKey} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {CatIcon && <CatIcon sx={{ fontSize: 18, color: cat.color }} />}
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: cat.color }}>
                  {catLabel}
                </Typography>
              </Box>
              <Grid container spacing={1}>
                {cat.items.map((item) => {
                  const isActive = !!activeItems[item.key];
                  const currentAmount = isActive ? activeItems[item.key].amount : item.defaultAmount;
                  return (
                    <Grid item xs={12} sm={6} key={item.key}>
                      <ExpenseToggleCard
                        itemKey={item.key}
                        catKey={catKey}
                        catColor={cat.color}
                        defaultAmount={item.defaultAmount}
                        frequency={item.frequency}
                        isActive={isActive}
                        currentAmount={currentAmount}
                        onToggle={handleToggle}
                        onAmountChange={handleAmountChange}
                        lng={lng}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}

        {/* Custom items */}
        {customItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              {lng === 'fr' ? 'Personnalis\u00e9s' : lng === 'en' ? 'Custom' : 'Personalizados'}
            </Typography>
            {customItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  mb: 0.5,
                  borderRadius: 2,
                  border: '2px solid #5F6B7A',
                  bgcolor: '#5F6B7A08',
                }}
              >
                <CheckCircleIcon fontSize="small" sx={{ color: '#5F6B7A' }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                  {item.name}
                </Typography>
                <Chip label={formatCAD(item.amount)} size="small" sx={{ fontWeight: 600 }} />
                <IconButton size="small" onClick={() => dispatch(removeFixedExpense(item.id))} sx={{ color: '#C62828' }} aria-label="Eliminar gasto">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <CustomItemInput onAdd={handleAddCustom} lng={lng} />

        {/* Running total footer */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1E3A5F' }}>
                {formatCAD(monthlyTotal)}/{lng === 'fr' ? 'mois' : lng === 'en' ? 'mo' : 'mes'}
              </Typography>
              {derived.monthlyIncome > 0 && (
                <Typography variant="caption" fontWeight={600} sx={{ color: ratioColor }}>
                  {Math.round(incomeRatio)}% {lng === 'fr' ? 'du revenu' : lng === 'en' ? 'of income' : 'del ingreso'}
                </Typography>
              )}
            </Box>
            {derived.monthlyIncome > 0 && (
              <LinearProgress
                variant="determinate"
                value={incomeRatio}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#E8E8E8',
                  '& .MuiLinearProgress-bar': { bgcolor: ratioColor, borderRadius: 3 },
                }}
              />
            )}
          </Box>
          <Chip
            label={`${state.fixedExpenses.length} ${lng === 'fr' ? '\u00e9l\u00e9ments' : lng === 'en' ? 'items' : 'gastos'}`}
            size="small"
            sx={{ bgcolor: '#F7F9FC', color: '#1E3A5F', fontWeight: 600 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
